<script setup lang="ts">
import { inject, onMounted, onUnmounted, ref, watch } from "vue";
import { CONNECTION } from "@/stores/connection";
import { LOGGER } from "@/stores/logger";
import { APP } from "@/stores/app";
import { JETSTREAM } from "@/stores/jetstream";

const log = inject(LOGGER)!;
const appStore = inject(APP)!;
const jetStreamStore = inject(JETSTREAM)!;
const connectionStore = inject(CONNECTION)!;

const jetstreamContainerRef = ref<HTMLElement>();

onMounted(async () => {
  await activate();
});

onUnmounted(async () => {
  await deactivate();
});

watch(
  () => appStore.active,
  async (active) => {
    if (active) {
      await activate();
    } else {
      await deactivate();
    }
  }
);

watch(
  () => appStore.prStringHtmlAttribute,
  async (newPrString) => {
    if (jetStreamStore.streamInitialized) {
      await changePrString(newPrString);
    } else {
      log.info(
        `PR string changed before app was initialized. PR string ${newPrString} will be loaded once stream is initialized.`
      );
    }
  }
);

async function activate() {
  if (!appStore.active) {
    // we should not activate if the app is not meant to be active
    return;
  }
  if (!jetstreamContainerRef.value) {
    // the HTML element to spawn the stream needs to exist
    return;
  }
  log.debug("Activating the 3D stream");
  await connectionStore.init();
  await connectionStore.connect(jetstreamContainerRef.value);
  await jetStreamStore.initializeStream();
  log.debug("Done activating the 3D stream");
}

async function deactivate() {
  log.debug("Deactivating the 3D stream");
  await connectionStore.disconnect();
}

async function changePrString(newPrString?: string) {
  if (appStore.active && newPrString) {
    log.debug("Switching car configuration");
    await jetStreamStore.selectCar(newPrString, appStore.consumptionDataHtmlAttribute);
  }
}
</script>

<template>
  <div ref="jetstreamContainerRef" data-testid="jetstream-container" class="deck-stream" />
</template>

<style>
.deck-stream {
  width: 100%;
  height: 100%;
}
</style>
